import { SESSION_CONFIG, getTimeoutMs, getWarningMs } from '../config/sessionConfig';

/**
 * Session Manager - Handles inactivity timeout and cross-tab synchronization
 */

class SessionManager {
  constructor() {
    this.timeoutId = null;
    this.warningTimeoutId = null;
    this.inactivityTime = getTimeoutMs(); // Configurable timeout
    this.warningTime = getWarningMs(); // Configurable warning time
    this.lastActivity = Date.now();
    this.isWarningShown = false;
    
    // Events that count as user activity
    this.activityEvents = SESSION_CONFIG.ACTIVITY_EVENTS;
    
    // Only initialize if inactivity logout is enabled
    if (SESSION_CONFIG.ENABLE_INACTIVITY_LOGOUT) {
      this.init();
    }
  }

  init() {
    // Listen for activity events
    this.activityEvents.forEach(event => {
      document.addEventListener(event, this.resetTimer.bind(this), true);
    });

    // Listen for storage changes (cross-tab communication)
    window.addEventListener('storage', this.handleStorageChange.bind(this));
    
    // Listen for visibility changes (tab switching)
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    
    // Start the timer
    this.resetTimer();
  }

  resetTimer() {
    this.lastActivity = Date.now();
    this.updateLastActivityInStorage();
    
    // Clear existing timers
    if (this.timeoutId) clearTimeout(this.timeoutId);
    if (this.warningTimeoutId) clearTimeout(this.warningTimeoutId);
    
    // Hide warning if shown
    if (this.isWarningShown) {
      this.hideInactivityWarning();
    }
    
    if (SESSION_CONFIG.DEBUG_MODE) {
      console.log('Session timer reset. Next warning in:', (this.inactivityTime - this.warningTime) / 1000 / 60, 'minutes');
    }
    
    // Set warning timer (25 minutes by default)
    this.warningTimeoutId = setTimeout(() => {
      this.showInactivityWarning();
    }, this.inactivityTime - this.warningTime);
    
    // Set logout timer (30 minutes by default)
    this.timeoutId = setTimeout(() => {
      this.logout();
    }, this.inactivityTime);
  }

  updateLastActivityInStorage() {
    localStorage.setItem('lastActivity', Date.now().toString());
  }

  handleStorageChange(event) {
    if (!SESSION_CONFIG.ENABLE_CROSS_TAB_SYNC) return;
    
    // Sync activity across tabs
    if (event.key === 'lastActivity') {
      const newActivity = parseInt(event.newValue);
      if (newActivity > this.lastActivity) {
        this.lastActivity = newActivity;
        this.resetTimer();
        
        if (SESSION_CONFIG.DEBUG_MODE) {
          console.log('Activity synced from another tab');
        }
      }
    }
    
    // Handle logout from another tab
    if (event.key === 'user' && !event.newValue) {
      // User logged out in another tab
      if (SESSION_CONFIG.DEBUG_MODE) {
        console.log('User logged out in another tab');
      }
      window.location.reload();
    }
  }

  handleVisibilityChange() {
    if (!document.hidden) {
      // Tab became visible, check if we need to logout
      const lastActivity = parseInt(localStorage.getItem('lastActivity') || '0');
      const timeSinceActivity = Date.now() - lastActivity;
      
      if (timeSinceActivity > this.inactivityTime) {
        this.logout();
      } else {
        // Update our timer based on cross-tab activity
        this.lastActivity = lastActivity;
        this.resetTimer();
      }
    }
  }

  showInactivityWarning() {
    this.isWarningShown = true;
    
    // Create warning modal
    const modal = document.createElement('div');
    modal.id = 'inactivity-warning';
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10000;
    `;
    
    modal.innerHTML = `
      <div style="
        background: white;
        padding: 30px;
        border-radius: 10px;
        text-align: center;
        max-width: 400px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.3);
      ">
        <h3 style="color: #dc3545; margin-bottom: 15px;">⚠️ Session Expiring</h3>
        <p style="margin-bottom: 20px;">
          You will be logged out in <span id="countdown">5:00</span> due to inactivity.
        </p>
        <button id="stay-logged-in" style="
          background: #007bff;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 5px;
          cursor: pointer;
          margin-right: 10px;
        ">Stay Logged In</button>
        <button id="logout-now" style="
          background: #6c757d;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 5px;
          cursor: pointer;
        ">Logout Now</button>
      </div>
    `;
    
    document.body.appendChild(modal);
    
    // Add event listeners
    document.getElementById('stay-logged-in').onclick = () => {
      this.hideInactivityWarning();
      this.resetTimer();
    };
    
    document.getElementById('logout-now').onclick = () => {
      this.logout();
    };
    
    // Start countdown
    this.startCountdown();
  }

  startCountdown() {
    let timeLeft = this.warningTime / 1000; // 5 minutes in seconds
    
    const countdownInterval = setInterval(() => {
      const minutes = Math.floor(timeLeft / 60);
      const seconds = timeLeft % 60;
      const display = `${minutes}:${seconds.toString().padStart(2, '0')}`;
      
      const countdownElement = document.getElementById('countdown');
      if (countdownElement) {
        countdownElement.textContent = display;
      }
      
      timeLeft--;
      
      if (timeLeft < 0 || !this.isWarningShown) {
        clearInterval(countdownInterval);
      }
    }, 1000);
  }

  hideInactivityWarning() {
    this.isWarningShown = false;
    const modal = document.getElementById('inactivity-warning');
    if (modal) {
      modal.remove();
    }
  }

  logout() {
    // Clear all timers
    if (this.timeoutId) clearTimeout(this.timeoutId);
    if (this.warningTimeoutId) clearTimeout(this.warningTimeoutId);
    
    // Hide warning
    this.hideInactivityWarning();
    
    // Clear user data
    localStorage.removeItem('user');
    localStorage.removeItem('lastActivity');
    
    // Redirect to login
    window.location.href = '/login';
  }

  destroy() {
    // Clean up event listeners
    this.activityEvents.forEach(event => {
      document.removeEventListener(event, this.resetTimer.bind(this), true);
    });
    
    window.removeEventListener('storage', this.handleStorageChange.bind(this));
    document.removeEventListener('visibilitychange', this.handleVisibilityChange.bind(this));
    
    // Clear timers
    if (this.timeoutId) clearTimeout(this.timeoutId);
    if (this.warningTimeoutId) clearTimeout(this.warningTimeoutId);
  }
}

export default SessionManager;